import React, { useMemo } from "react";
import { resolveRmsPipelineStatus } from "./rmsPipelineStatus";

const STAGE_LABEL_KEYS = Object.freeze({
  cancellation_curves: "Cancellation curves",
  demand_forecast: "Demand forecast",
  daily_features: "Daily features",
  recommendations: "Recommendations",
  long_range_price_guidance: "Long-range guidance",
});

function formatTimestamp(value, locale) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString(locale || "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function issueText(issue, t) {
  const stage = issue.stage
    ? t(STAGE_LABEL_KEYS[issue.stage] || issue.stage)
    : t("the pipeline");
  const section = issue.section || stage;
  const messages = {
    pipeline_unavailable: "No published pipeline is available.",
    pipeline_not_published: "The pipeline has not been published.",
    pipeline_incomplete: "The pipeline has not completed every required stage.",
    stage_run_missing: "The {stage} stage has no run linked to this pipeline.",
    stage_run_time_mismatch:
      "The {stage} stage run falls outside this pipeline's execution window.",
    stage_run_range_mismatch:
      "The {stage} stage run uses a different date range from this pipeline.",
    newer_pipeline_running: "A newer pipeline is still running.",
    newer_pipeline_failed: "A newer pipeline attempt failed.",
    legacy_run_without_pipeline: "{section} contains legacy data without a pipeline ID.",
    requested_run_not_published_pipeline:
      "The requested run does not belong to the published pipeline.",
    requested_run_not_found: "The requested run could not be found.",
    pipeline_running: "The pipeline is still running.",
    pipeline_error: "The pipeline finished with an error.",
    pipeline_not_current: "The published pipeline is no longer current.",
    pipeline_marked_stale: "The pipeline was marked stale without further detail.",
    pipeline_endpoint_mismatch: "{section} belongs to a different pipeline.",
    pipeline_section_mismatch: "{section} contains data from a different pipeline.",
    pipeline_section_stale: "{section} is marked stale.",
    pipeline_contract_invalid: "Pipeline metadata is missing or invalid.",
  };
  const message = messages[issue.code] || "Pipeline reported: {reason}";
  return t(message, { stage, section, reason: issue.code });
}

export default function RmsPipelineStatus({
  pipeline,
  t,
  locale,
  loading = false,
  relatedPipelines = [],
  recordGroups = [],
}) {
  const status = useMemo(
    () => resolveRmsPipelineStatus(pipeline, { relatedPipelines, recordGroups }),
    [pipeline, recordGroups, relatedPipelines]
  );

  if (loading) {
    return (
      <div className="rmo-pipelineStatus rmo-pipelineStatus--loading" role="status">
        <span className="rmo-pill rmo-pill--soft">{t("Checking pipeline...")}</span>
        <span className="rmo-pipelineMeta">{t("Verifying that every section uses one published snapshot.")}</span>
      </div>
    );
  }

  const details = status.pipeline;
  const latestAttempt = details?.latestAttempt || status.latestAttempt;
  const visibleIssues = status.issues.slice(0, 6);
  const hiddenIssueCount = Math.max(0, status.issues.length - visibleIssues.length);

  return (
    <div
      className={`rmo-pipelineStatus rmo-pipelineStatus--${status.state}`}
      role="status"
      aria-live="polite"
    >
      <div className="rmo-pipelineHeader">
        <span className={`rmo-pill rmo-pill--${status.tone}`}>{t(status.labelKey)}</span>
        {details ? (
          <span className="rmo-pipelineIdentity">
            {t("Pipeline #{id}", { id: details.id })} · {t("Published {date}", {
              date: formatTimestamp(details.publishedAt, locale),
            })}
          </span>
        ) : null}
      </div>

      {details ? (
        <div className="rmo-pipelineMeta">
          {t("Status: {status}", { status: t(details.status) })} · {t("Stages {completed}/{expected}", {
            completed: details.completedStages.length,
            expected: details.expectedStages.length,
          })} · {details.dateFrom} → {details.dateTo}
        </div>
      ) : (
        <div className="rmo-pipelineMeta">
          {t("This response cannot prove that all RMS sections come from one published snapshot.")}
        </div>
      )}

      {latestAttempt &&
      (latestAttempt.id !== details?.id || latestAttempt.status !== "ok") ? (
        <>
          <div className="rmo-pipelineAttempt">
            {t("Latest attempt #{id}: {status}", {
              id: latestAttempt.id,
              status: t(latestAttempt.status),
            })} · {formatTimestamp(latestAttempt.startedAt, locale)}
          </div>
          {latestAttempt.errorMessage ? (
            <div className="rmo-pipelineAttempt">
              {t("Latest attempt error: {message}", {
                message: latestAttempt.errorMessage,
              })}
            </div>
          ) : null}
        </>
      ) : null}

      {visibleIssues.length ? (
        <ul className="rmo-pipelineIssues">
          {visibleIssues.map((issue) => (
            <li key={issue.key || `${issue.code}-${issue.stage || "pipeline"}`}>
              {issueText(issue, t)}
            </li>
          ))}
          {hiddenIssueCount ? (
            <li>{t("{count} more pipeline issues", { count: hiddenIssueCount })}</li>
          ) : null}
        </ul>
      ) : (
        <div className="rmo-pipelineConfirmed">
          {t("All visible RMS sections belong to this published pipeline.")}
        </div>
      )}
    </div>
  );
}
